import type {ExploreNode, Risk} from "../types/categoryProfileTypes";

export const PATH_SEPARATOR = '|';

export type RiskFilter = 'all' | Risk;
/** 'all' shows the scope's sub-tree; a number shows the categories at that level under the scope, flat. */
export type LevelFilter = 'all' | number;

export type ExploreRow = ExploreNode & {
    /** Labels below the scope, for the grid's tree data (`getTreeDataPath`). */
    treePath: string[];
    /** Parent path shown under a root row's name. */
    parentPath: string;
    rank: number;
    /** Shown only because a category below it matches the risk filter (dimmed in the table). */
    riskContext?: boolean;
};

export function labelsOf(path: string): string[] {
    return path ? path.split(PATH_SEPARATOR) : [];
}

export function findNode(tree: ExploreNode[], path: string): ExploreNode | undefined {
    let nodes = tree;
    let found: ExploreNode | undefined;
    for (const label of labelsOf(path)) {
        found = nodes.find(n => n.name === label);
        if (!found) return undefined;
        nodes = found.children;
    }
    return found;
}

/** The selection, trimmed to the existing part of the path (unknown segments are dropped, spec 3.7). */
export function validPath(tree: ExploreNode[], path: string): string {
    const kept: string[] = [];
    for (const label of labelsOf(path)) {
        if (!findNode(tree, [...kept, label].join(PATH_SEPARATOR))) break;
        kept.push(label);
    }
    return kept.join(PATH_SEPARATOR);
}

export function scopeChildren(tree: ExploreNode[], scopePath: string): ExploreNode[] {
    return scopePath ? findNode(tree, scopePath)?.children ?? [] : tree;
}

function walk(nodes: ExploreNode[], visit: (n: ExploreNode) => void) {
    for (const n of nodes) {
        visit(n);
        walk(n.children, visit);
    }
}

/** The tree's top rows for a scope: every L1 for no selection, else the selected category itself. */
function scopeRoots(tree: ExploreNode[], scopePath: string): ExploreNode[] {
    if (!scopePath) return tree;
    const node = findNode(tree, scopePath);
    return node ? [node] : [];
}

/**
 * Grid rows for a scope. Level 'all': the selected category (or every L1) with its whole sub-tree (tree data, every
 * branch expandable), so a selected leaf is one row. A level number: only the categories at that level in the scope,
 * flat, ranked by spend. The risk filter (spec 3.5) applies at every level: matching categories stay with the
 * categories above them for context (`riskContext`); the selected category itself always stays in the table.
 */
export function exploreRows(tree: ExploreNode[], scopePath: string, level: LevelFilter, risk: RiskFilter): ExploreRow[] {
    const byRisk = (n: ExploreNode) => risk === 'all' || n.risk === risk;
    // A category stays when it matches, or when something below it does.
    const keep = (n: ExploreNode): boolean => byRisk(n) || n.children.some(keep);
    const bySpend = (a: ExploreNode, b: ExploreNode) => b.spend - a.spend;
    const roots = scopeRoots(tree, scopePath);
    const tops = level !== 'all'
        ? collectAtLevel(roots, level).filter(byRisk).sort(bySpend)
        : scopePath ? roots : roots.filter(keep).sort(bySpend);
    const rows: ExploreRow[] = [];
    tops.forEach((top, i) => {
        const topLabels = labelsOf(top.path);
        const parentPath = topLabels.slice(0, -1).join(' › ');
        const add = (n: ExploreNode, rank: number) => {
            rows.push({...n, treePath: labelsOf(n.path).slice(topLabels.length - 1), parentPath, rank,
                riskContext: risk !== 'all' && !byRisk(n)});
        };
        const addBranch = (n: ExploreNode) => {
            add(n, 0);
            n.children.filter(keep).sort(bySpend).forEach(addBranch);
        };
        add(top, i + 1);
        if (level !== 'all') return;
        top.children.filter(keep).sort(bySpend).forEach(addBranch);
    });
    return rows;
}

/** How many categories in the rows match the risk filter (the rows kept only for context do not count). */
export function matchingCount(rows: ExploreRow[]): number {
    return rows.filter(r => !r.riskContext).length;
}

function collectAtLevel(nodes: ExploreNode[], level: number): ExploreNode[] {
    const out: ExploreNode[] = [];
    walk(nodes, n => {
        if (n.level === level) out.push(n);
    });
    return out;
}

/** Levels in the table for a scope (the selected category's own level and those below it), for the level filter. */
export function levelsBelow(tree: ExploreNode[], scopePath: string): number[] {
    const levels = new Set<number>();
    walk(scopeRoots(tree, scopePath), n => levels.add(n.level));
    return [...levels].sort((a, b) => a - b);
}

export type CompositionSegment = {key: string, path: string | null, spend: number, sharePct: number};

/** Top 5 children by spend plus "Other", for the navigator's composition bar (spec 3.2). */
export function composition(children: ExploreNode[], top = 5): CompositionSegment[] {
    const total = children.reduce((s, n) => s + n.spend, 0);
    if (!total) return [];
    const sorted = [...children].sort((a, b) => b.spend - a.spend);
    const segments: CompositionSegment[] = sorted.slice(0, top).map(n => ({
        key: n.name, path: n.path, spend: n.spend, sharePct: n.spend / total * 100,
    }));
    const other = sorted.slice(top).reduce((s, n) => s + n.spend, 0);
    if (other > 0) segments.push({key: 'Other', path: null, spend: other, sharePct: other / total * 100});
    return segments;
}

/** "Top 3 = X% of spend" in the navigator's insights block. */
export function topShare(children: ExploreNode[], n = 3): number | null {
    const total = children.reduce((s, c) => s + c.spend, 0);
    if (!total) return null;
    const top = [...children].sort((a, b) => b.spend - a.spend).slice(0, n).reduce((s, c) => s + c.spend, 0);
    return top / total * 100;
}

export function breadcrumb(path: string): {label: string, path: string}[] {
    const labels = labelsOf(path);
    return labels.map((label, i) => ({label, path: labels.slice(0, i + 1).join(PATH_SEPARATOR)}));
}

/**
 * How deep the tree opens for a level filter (MUI `defaultGroupingExpansionDepth`): 'all' expands every branch (-1);
 * a level shows the tree down to that level, so the scope's top level is depth 0. `levels` are the levels under the
 * scope, lowest first.
 */
export function expansionDepth(level: LevelFilter, levels: number[]): number {
    if (level === 'all') return -1;
    const top = levels[0] ?? level;
    return Math.max(0, level - top);
}

/** Paths of every category that has sub-categories, to open a tree fully expanded. */
export function branchPaths(nodes: ExploreNode[]): string[] {
    const out: string[] = [];
    walk(nodes, n => {
        if (n.children.length) out.push(n.path);
    });
    return out;
}
